import { Check, Loader2, X, Sparkles } from 'lucide-react'

export interface DraftAssumption {
  id: string
  questionId: string
  question: string
  recommendedAnswer: string | null
  sectionKey: string
  controlRef: string | null
  blueprintInstanceIds: string[]
  docBlockId: string | null
  status: 'pending' | 'confirmed' | 'dismissed'
  decidedAt?: string | null
  decidedBy?: string | null
}

export function AssumptionCard({
  assumption,
  isSelected,
  isResolving,
  onClick,
  onConfirm,
  onDismiss,
}: {
  assumption: DraftAssumption
  isSelected: boolean
  isResolving: boolean
  onClick: () => void
  onConfirm: () => void
  onDismiss: () => void
}) {
  const isPending = assumption.status === 'pending'
  const isConfirmed = assumption.status === 'confirmed'
  const isDismissed = assumption.status === 'dismissed'

  return (
    <div
      data-assumption-id={assumption.id}
      onClick={onClick}
      className={`cursor-pointer rounded-md border bg-card px-3 py-2.5 transition-colors ${
        isSelected
          ? 'border-ring shadow-[0_0_0_2px_rgba(63,95,206,0.14)]'
          : 'border-border hover:border-border-divider'
      }`}
    >
      <div className='mb-1.5 flex items-start justify-between gap-2'>
        <span className='inline-flex items-center gap-1 rounded-sm bg-status-orange-bg px-1.5 py-0.5 text-[9px] font-semibold tracking-wider text-status-orange uppercase'>
          <Sparkles className='h-2.5 w-2.5' />
          Assumption
        </span>
        {assumption.controlRef && (
          <span className='font-heading rounded-sm bg-primary-subtle px-1.5 py-0.5 text-[9px] font-semibold text-cobalt-strong'>
            {assumption.controlRef}
          </span>
        )}
      </div>

      <p className='font-heading mb-1.5 text-[12px] leading-[1.4] font-medium text-text-secondary break-words'>
        {assumption.question}
      </p>

      {assumption.recommendedAnswer && (
        <div className='mb-2 rounded-sm border border-border bg-surface-subtle px-2 py-1.5'>
          <div className='font-body mb-0.5 text-[9px] font-semibold tracking-[0.5px] text-text-subtle uppercase'>
            Assumed answer
          </div>
          <p className='font-body text-[12px] leading-[1.4] text-text-body break-words'>
            {assumption.recommendedAnswer}
          </p>
        </div>
      )}

      {isPending ? (
        <div className='flex items-center gap-1.5'>
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation()
              if (!isResolving) onConfirm()
            }}
            disabled={isResolving}
            className='font-heading inline-flex h-[26px] flex-1 items-center justify-center gap-1 rounded-sm bg-primary px-2 text-[11px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-60'
          >
            {isResolving ? (
              <Loader2 className='h-3 w-3 animate-spin' />
            ) : (
              <Check className='h-3 w-3' />
            )}
            Confirm
          </button>
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation()
              if (!isResolving) onDismiss()
            }}
            disabled={isResolving}
            className='font-heading inline-flex h-[26px] flex-1 items-center justify-center gap-1 rounded-sm border border-border bg-card px-2 text-[11px] font-medium text-text-body transition-colors hover:bg-surface-muted disabled:opacity-60'
          >
            <X className='h-3 w-3' />
            Dismiss
          </button>
        </div>
      ) : (
        <div
          className={`font-heading inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
            isConfirmed
              ? 'bg-status-green-bg text-status-green'
              : 'bg-muted text-text-subtle'
          }`}
        >
          {isConfirmed ? <Check className='h-2.5 w-2.5' /> : <X className='h-2.5 w-2.5' />}
          {isConfirmed ? 'Confirmed' : 'Dismissed'}
        </div>
      )}
    </div>
  )
}
